import React, { useState, useEffect } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProductDetail, selectProductDetailState, resetProductDetailState, addToCart } from '../redux/WithcookieSlice';
import { formatPrice, formatKRW } from '../utils/format';
import { PriceDisplay, DiscountedPriceDisplay } from './common/PriceDisplay';
import SafeImage from './common/SafeImage';
import { useTranslation } from '../redux/TranslationHelper';

// 오류 객체를 문자열로 변환하는 함수
const errorToString = (err) => {
  if (!err) return '';
  if (typeof err === 'string') return err;
  if (typeof err === 'object') {
    return err.message || JSON.stringify(err);
  }
  return String(err);
};

const ProductDetailComponent = ({ componentProps, pathVariables = {} }) => {
  // 다국어 지원 훅 사용
  const { t } = useTranslation();
  
  // URL 경로 변수에서 productId를 우선적으로 가져오고, 없으면 componentProps의 값 사용
  const { 
    order = "", 
    none = "none", 
    lg = 9, 
    productId: propsProductId = 0,
    showDescriptionTab = true,
    showReviewsTab = true,
    showSpecificationTab = true,
    showStock = false
  } = componentProps || {};
  
  const dispatch = useDispatch();
  const { productData, loading, error } = useSelector(selectProductDetailState);
  
  // 경로 변수에서 id를 가져와서 사용
  const productId = pathVariables?.id 
    ? parseInt(pathVariables.id, 10) 
    : propsProductId;

  const [quantity, setQuantity] = useState(1);
  
  // 보이는 탭 중 첫 번째 탭을 기본값으로 설정
  const getInitialActiveTab = () => {
    if (showDescriptionTab) return 'description';
    if (showReviewsTab) return 'reviews';
    if (showSpecificationTab) return 'specification';
    return 'description'; // 모든 탭이 숨겨진 경우 (실제로는 표시되지 않음)
  };
  
  const [activeTab, setActiveTab] = useState(getInitialActiveTab());
  
  // 탭 표시 여부가 변경될 때 활성 탭 업데이트
  useEffect(() => {
    // 현재 활성 탭이 표시되지 않는 경우 다른 탭으로 변경
    if (
      (activeTab === 'description' && !showDescriptionTab) ||
      (activeTab === 'reviews' && !showReviewsTab) ||
      (activeTab === 'specification' && !showSpecificationTab)
    ) {
      setActiveTab(getInitialActiveTab());
    }
  }, [showDescriptionTab, showReviewsTab, showSpecificationTab, activeTab]);
  
  // 컴포넌트 언마운트 시 상품 상세 상태 초기화
  useEffect(() => {
    return () => {
      dispatch(resetProductDetailState());
    };
  }, [dispatch]);
  
  // API에서 상품 데이터 가져오기
  useEffect(() => {
    if (productId) {
      dispatch(fetchProductDetail(productId));
    }
  }, [dispatch, productId]); // productId가 변경될 때마다 데이터 다시 가져오기
  
  const increaseQuantity = () => {
    setQuantity(quantity + 1);
  };

  const decreaseQuantity = () => {
    if (quantity > 1) {
      setQuantity(quantity - 1);
    }
  };

  const StarRating = ({ rating = 4 }) => {
    // rating이 숫자인지 확인하고 유효한 범위(0-5)로 제한
    const safeRating = typeof rating === 'number' && !isNaN(rating) 
      ? Math.min(Math.max(0, rating), 5) 
      : 4;
    
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= safeRating) {
        stars.push(<i key={i} className="gicon gi-star fill"></i>);
      } else {
        stars.push(<i key={i} className="gicon gi-star-o"></i>);
      }
    }
    return <>{stars}</>;
  };

  const QuantitySelector = () => {
    return (
      <div className="quantity-selector">
        <span className="quantity-label"></span>
        <button className="qty-down" onClick={decreaseQuantity}>-</button>
        <input className="qty-input" type="number" value={quantity} readOnly />
        <button className="qty-up" onClick={increaseQuantity}>+</button>
      </div>
    );
  };
  
  // 마크다운 형식의 이미지 URL 추출
  const extractImagesFromMarkdown = (markdown) => {
    if (!markdown) return [];
    const regex = /!\[.*?\]\((.*?)\)/g;
    const matches = [];
    let match;
    
    while ((match = regex.exec(markdown)) !== null) {
      matches.push(match[1]);
    }
    
    return matches;
  };
  
  // 로딩 중 표시
  if (loading) {
    return (
      <div className="product-loading" style={{padding: "20px", textAlign: "center"}}>
        <p>{t('상품 정보를 불러오는 중입니다...')}</p>
      </div>
    );
  }
  
  // 오류 발생 시 표시
  if (error && !productData) {
    const errorMessage = errorToString(error);
    return (
      <div className="product-error" style={{padding: "20px", textAlign: "center", color: "red"}}>
        <p>{errorMessage}</p>
      </div>
    );
  }
  
  // 데이터가 없는 경우
  if (!productData) {
    return (
      <div className="no-product-data" style={{padding: "20px", textAlign: "center"}}>
        <p>{t('상품 정보를 찾을 수 없습니다.')}</p>
      </div>
    );
  }
  
  // 설명에서 이미지 추출
  const descriptionImages = extractImagesFromMarkdown(productData.product?.description || productData.description);

  // 장바구니 추가 함수
  const handleAddToCart = () => {
    if (productData && productId) {
      try {
        dispatch(addToCart({ productId, count: quantity }))
          .unwrap()
          .then(() => {
            // 성공 메시지
            const message = t('상품이 장바구니에 추가되었습니다.') + ` (${quantity}` + t('개') + `)`;
            alert(message);
            
            // 옵션: 장바구니 페이지로 이동하거나 현재 페이지에 남을 수 있도록 확인
            if (window.confirm(t('장바구니 페이지로 이동하시겠습니까?'))) {
              window.location.href = '/cart';
            }
          })
          .catch((error) => {
            console.error('장바구니 추가 오류:', error);
            
            // 로그인이 필요한 경우
            if (error.code === 'AUTH_REQUIRED') {
              if (window.confirm(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동하시겠습니까?'))) {
                window.location.href = '/login';
              }
            } else {
              // 기타 오류
              alert(t('장바구니 추가 실패') + ': ' + errorToString(error));
            }
          });
      } catch (error) {
        console.error('예상치 못한 오류:', error);
        alert(t('예상치 못한 오류') + ': ' + errorToString(error));
      }
    } else {
      alert(t('상품 정보가 유효하지 않습니다.'));
    }
  };

  return (
    <>
      <style>
        {`
          @media only screen and (min-width: 990px) and (max-width: 1200px) {
            /* 태블릿 스타일을 990px-1200px 범위에 적용 */
            .gi-single-product .col-lg-3 {
              display: none !important;
            }
            
            .gi-single-product .col-lg-6 {
              -ms-flex: 0 0 100%;
              flex: 0 0 100%;
              max-width: 100%;
            }
            
            /* 이미지와 정보가 가로로 배치되도록 수정 */
            .gi-single-product .single-pro-inner .row {
              display: flex !important;
              flex-direction: row !important;
            }
            
            /* 이미지 컬럼 */
            .gi-single-product .single-pro-img {
              -ms-flex: 0 0 50%;
              flex: 0 0 50%;
              max-width: 50%;
              padding-right: 15px;
            }
            
            /* 상품 정보 컬럼 */
            .gi-single-product .single-pro-desc {
              -ms-flex: 0 0 50%;
              flex: 0 0 50%;
              max-width: 50%;
              padding-left: 15px;
            }
            
            .gi-single-product .gi-pro-tab-nav {
              flex-wrap: wrap;
            }
            
            .gi-single-product .gi-pro-tab-nav .nav-item {
              margin-bottom: 10px;
            }
            
            .gi-single-product .gi-description-detail p {
              width: 100%;
            }
            
            .gi-single-product .gi-single-price-stoke {
              margin-bottom: 15px;
            }
            
            .gi-single-product .gi-single-price {
              margin-bottom: 5px;
            }
            
            .gi-single-product .gi-single-qty {
              margin-top: 20px;
            }
            
            .gi-single-product .product-details-content {
              margin-top: 0;
            }
          }
        `}
      </style>
      <section className="gi-single-product padding-tb-40" style={{backgroundColor: "#fff", width: "100%", maxWidth: "100%"}}>
        <div className="container-fluid">
          <Row className="justify-content-center">
            <Col
              lg={7}
              md={12}
              className={`gi-pro-rightside gi-common-rightside ${order}`}
              style={{maxWidth: "100%", padding: "0 15px"}}
            >
              {/* Single product content Start */}
              <div className="single-pro-block">
                <div className="single-pro-inner">
                  <Row>
                    <Col sm={12} md={6} className="single-pro-img">
                      <div className="single-product-scroll">
                        {/* 메인 이미지 직접 표시 */}
                        <div className="main-product-image">
                          <SafeImage 
                            src={productData.product?.config?.img_url || 
                                productData.product?.mainImage || 
                                productData.mainImage} 
                            alt={productData.product?.title || productData.title || t('상품명')} 
                            style={{width: "100%", height: "auto", borderRadius: "5px", marginBottom: "15px"}} 
                            placeholderStyle={{
                              width: "100%",
                              paddingTop: "75%", // 4:3 비율
                              backgroundColor: "#cccccc",
                              borderRadius: "5px",
                              marginBottom: "15px",
                              position: "relative"
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                    <Col sm={12} md={6} className="single-pro-desc">
                      <div className="single-pro-content">
                        <h5 className="gi-single-title">
                          {t(productData.product?.title) || t(productData.title) || t('상품명')}
                        </h5>
                        <div className="gi-single-rating-wrap">
                          <div className="gi-single-rating">
                            <StarRating rating={productData.product?.averageRating || 0} />
                          </div>
                     
                        </div>

                        <div className="gi-single-price-stoke">
                          <div className="gi-single-price">
                            <div className="final-price">
                              <PriceDisplay 
                                price={productData.product?.config?.discounted_price || 
                                  productData.product?.config?.default_price || 
                                  productData.price || 0} 
                              />
                              {productData.discount && productData.discount !== "0%" && (
                                <span className="price-des">-{productData.discount}</span>
                              )}
                            </div>
                            {productData.product?.config?.default_price > productData.product?.config?.discounted_price && (
                              <div className="mrp">
                                M.R.P. : <PriceDisplay 
                                  price={productData.product?.config?.default_price || productData.oldPrice || 0}
                                />
                              </div>
                            )}
                          </div>
                          {showStock && (
                            <div className="gi-single-stoke">
                              <span className="gi-single-sku">SKU#: {productData.sku || productData.product?.id || ""}</span>
                              <span className="gi-single-ps-title">
                                {productData.product?.config?.stock_count ? 
                                  `재고: ${productData.product.config.stock_count}개` : 
                                  productData.stock || "재고 정보 없음"}
                              </span>
                            </div>
                          )}
                        </div>
                        
                        {/* 태그 정보 있으면 표시 */}
                        {(productData.product?.tags || productData.tags) && (productData.product?.tags || productData.tags).trim() !== "" && (
                          <div className="gi-single-tags" style={{margin: "10px 0"}}>
                            <strong>태그:</strong> {productData.product?.tags || productData.tags}
                          </div>
                        )}

                        {/* 간단한 설명 (첫 100자) */}
                        {/* <div className="gi-single-desc">
                          {productData.product?.description ? 
                            (!productData.product.description.startsWith("![") ? 
                              productData.product.description.substring(0, 100) + (productData.product.description.length > 100 ? "..." : "") : 
                              "상세 이미지를 참고해 주세요.")
                            : productData.description && !productData.description.startsWith("![") ? 
                              productData.description.substring(0, 100) + (productData.description.length > 100 ? "..." : "") : 
                              "상세 이미지를 참고해 주세요."
                          }
                        </div> */}

                        {productData.features && productData.features.length > 0 && (
                          <div className="gi-single-list">
                            <ul>
                              {productData.features.map((feature, index) => (
                                <li key={index}>
                                  <strong>{feature.label} :</strong> {feature.value}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {productData.weights && productData.weights.length > 0 && (
                          <div className="gi-pro-variation">
                            <div className="gi-pro-variation-inner gi-pro-variation-size">
                              <span>Weight</span>
                              <div className="gi-pro-variation-content">
                                <ul>
                                  {productData.weights.map((weight, index) => (
                                    <li key={index} className={index === 0 ? "active" : ""}>
                                      <span>{weight}</span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        <div className="gi-single-qty">
                          <div className="qty-plus-minus">
                            <QuantitySelector />
                          </div>
                          <div className="gi-single-cart">
                            <button className="btn btn-primary gi-btn-1" onClick={handleAddToCart}>
                              {t('장바구니 추가')}
                            </button>
                          </div>
                          {/* <div className="gi-single-wishlist">
                            <a className="gi-btn-group wishlist" title="Wishlist" href="#">
                              <i className="fi-rr-heart"></i>
                            </a>
                          </div> */}
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>
              </div>
              {/* Single product content End */}
              
              {/* Product Tab Start */}
              {(showDescriptionTab || showReviewsTab || showSpecificationTab) && (
                <div className="gi-pro-tab gi-pro-tab-style1" style={{width: "100%"}}>
                  <ul className="nav nav-tabs gi-pro-tab-nav" role="tablist" style={{width: "100%"}}>
                    {showDescriptionTab && (
                      <li className="nav-item">
                        <a 
                          className={`nav-link ${activeTab === 'description' ? 'active' : ''}`} 
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveTab('description');
                          }}
                          href="#" 
                          role="tab"
                        >
                          {t('상품 설명')}
                        </a>
                      </li>
                    )}
                    {showReviewsTab && (
                      <li className="nav-item">
                        <a 
                          className={`nav-link ${activeTab === 'reviews' ? 'active' : ''}`} 
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveTab('reviews');
                          }}
                          href="#" 
                          role="tab"
                        >
                          {t('리뷰')}
                        </a>
                      </li>
                    )}
                    {/* {showSpecificationTab && (
                      <li className="nav-item">
                        <a 
                          className={`nav-link ${activeTab === 'specification' ? 'active' : ''}`} 
                          onClick={(e) => {
                            e.preventDefault();
                            setActiveTab('specification');
                          }}
                          href="#" 
                          role="tab"
                        >
                          {t('상품 스펙')}
                        </a>
                      </li>
                    )} */}
                  </ul>
                  <div className="tab-content gi-pro-tab-content">
                    {showDescriptionTab && (
                      <div className={`tab-pane fade ${activeTab === 'description' ? 'show active' : ''}`} id="description" role="tabpanel">
                        <div className="gi-description-block">
                          <div className="gi-description-detail">
                            {descriptionImages.length > 0 ? (
                              <div className="description-images">
                                {descriptionImages.map((imgUrl, index) => (
                                  <div key={index} style={{marginBottom: "15px"}}>
                                    <SafeImage 
                                      src={imgUrl} 
                                      alt={t('상품 상세 이미지') + ' ' + (index + 1)} 
                                      style={{width: "100%", height: "auto"}} 
                                      placeholderStyle={{
                                        width: "100%",
                                        paddingTop: "75%", // 4:3 비율
                                        backgroundColor: "#cccccc",
                                        marginBottom: "15px",
                                        position: "relative"
                                      }}
                                    />
                                  </div>
                                ))}
                              </div>
                            ) : productData.product?.description ? (
                              <div dangerouslySetInnerHTML={{ __html: productData.product.description.replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" style="width:100%; height:auto; margin:10px 0;" />') }} />
                            ) : productData.description ? (
                              <div dangerouslySetInnerHTML={{ __html: productData.description.replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" style="width:100%; height:auto; margin:10px 0;" />') }} />
                            ) : (
                              <>
                                <p>{t('상품 설명이 없습니다.')}</p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {showReviewsTab && (
                      <div className={`tab-pane fade ${activeTab === 'reviews' ? 'show active' : ''}`} id="reviews" role="tabpanel">
                        <div className="gi-desc-reviews">
                          <div className="gi-reviews-wrapper">
                            <h4 className="gi-reviews-title">{t('Customer Reviews')}</h4>
                            {productData.product?.recentReviews && productData.product.recentReviews.length > 0 ? (
                              <div className="gi-review-box">
                                {productData.product.recentReviews.map((review, index) => (
                                  <div className="gi-review" key={index}>
                                    
                                    <div className="gi-review-content">
                                      <div className="gi-review-top">
                                        <div className="gi-review-name">{review.userName || t("사용자")}</div>
                                        <div className="gi-rating-star">
                                          <StarRating rating={review.rating || 0} />
                                        </div>
                                        <div className="gi-review-date">
                                          {new Date(review.createdAt).toLocaleDateString()}
                                        </div>
                                      </div>
                                      <div className="gi-review-bottom">
                                        <div dangerouslySetInnerHTML={{ __html: review.content }} />
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="gi-review-box">
                                <p>{t('아직 리뷰가 없습니다.')}</p>
                              </div>
                            )}
                            {productData.product?.totalReviews > 0 && (
                              <div className="gi-review-stats">
                                <p>{t('총 리뷰 수')}: {productData.product.totalReviews}{t('개')}</p>
                                <p>{t('평균 평점')}: <StarRating rating={productData.product.averageRating || 0} /> ({productData.product.averageRating ? productData.product.averageRating.toFixed(1) : '0.0'})</p>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                    {showSpecificationTab && (
                      <div className={`tab-pane fade ${activeTab === 'specification' ? 'show active' : ''}`} id="specification" role="tabpanel">
                        <div className="gi-specification-block">
                          <div className="gi-specification-box">
                            <h3>{t('Specification')}</h3>
                            <table className="table gi-specification-table">
                              <tbody>
                                <tr>
                                  <td>{t('Brand')}</td>
                                  <td>{t('Brand Name')}</td>
                                </tr>
                                <tr>
                                  <td>{t('Product Code')}</td>
                                  <td>{productData.sku || productData.product?.id || ""}</td>
                                </tr>
                                <tr>
                                  <td>{t('Weight')}</td>
                                  <td>{productData.weights && productData.weights.length > 0 ? productData.weights[0] : "N/A"}</td>
                                </tr>
                                <tr>
                                  <td>{t('Packaging')}</td>
                                  <td>{t('Single Unit')}</td>
                                </tr>
                                <tr>
                                  <td>{t('Shelf Life')}</td>
                                  <td>{t('12 Months')}</td>
                                </tr>
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Product Tab End */}
            </Col>
            
            {/* Optional Sidebar */}
            {none !== "none" && (
              <Col lg={3} md={12} className={`gi-common-leftside ${order}`}>
                <div className="gi-sidebar-wrapper">
                  <div className="gi-sidebar-block">
                    <div className="gi-sidebar-title">
                      <h3>Categories</h3>
                    </div>
                    <div className="gi-sidebar-content">
                      <ul className="gi-category-list">
                        <li><a href="#">Snacks</a></li>
                        <li><a href="#">Beverages</a></li>
                        <li><a href="#">Dairy</a></li>
                        <li><a href="#">Fruits & Vegetables</a></li>
                      </ul>
                    </div>
                  </div>
                  {productData.relatedProducts && Array.isArray(productData.relatedProducts) && productData.relatedProducts.length > 0 && (
                    <div className="gi-sidebar-block">
                      <div className="gi-sidebar-title">
                        <h3>Popular Products</h3>
                      </div>
                      <div className="gi-sidebar-content">
                        <ul className="gi-popular-product">
                          {productData.relatedProducts.slice(0, 3).map((product, index) => (
                            <li key={index}>
                              <div className="gi-pro-image">
                                <SafeImage 
                                  src={product.image} 
                                  alt={product.title || "관련 상품"} 
                                  placeholderStyle={{ width: "100%", height: "100px", backgroundColor: "#cccccc" }}
                                />
                              </div>
                              <div className="gi-pro-content">
                                <h5>{product.title || "상품명"}</h5>
                                <div className="gi-rating-star">
                                  <StarRating rating={typeof product.rating === 'number' ? product.rating : 5} />
                                </div>
                                <span className="gi-price">
                                  <span className="new-price">${typeof product.newPrice === 'number' ? product.newPrice.toFixed(2) : "0.00"}</span>
                                  <span className="old-price">${typeof product.oldPrice === 'number' ? product.oldPrice.toFixed(2) : "0.00"}</span>
                                </span>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              </Col>
            )}
          </Row>
        </div>
      </section>
    </>
  );
};

export default ProductDetailComponent; 